// @flow

import React, { useEffect, useState } from "react";
import { localize } from "redux-i18n";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  Icon,
  View,
  Text,
  TextField,
  Label,
} from "@components";
import { emailPattern } from "@utils/schema";
import { unpatch } from "@transformers/js-client";
import snakecase from "snakecase-keys";
import { getEmployeeFormData } from "@modules/employee/selectors";
import employeeActionCreators from "@modules/employee/actionCreator";
import type { EmployeeFormData } from "@modules/employee/types";

type PropsFromState = $Exact<{
  employeeFormData: EmployeeFormData,
}>;

type PropsFromDispatch = $Exact<{
  getEmployees: typeof employeeActionCreators.GetEmployees,
  onUpdateEmployee: typeof employeeActionCreators.UpdateEmployeeDetails,
}>;

type Props = {
  ...PropsFromLocalize,
  ...PropsFromDispatch,
  ...PropsFromState,
  clientId: number,
  employeeId: number,
  employeeFormData: EmployeeFormData,
  onClose: () => void,
};

const SetMissingEmailModal = ({ employeeFormData, ...props }: Props) => {
  const [email, setEmail] = useState("");

  useEffect(() => {
    props.getEmployees({
      clientId: props.clientId,
      filters: {
        id: props.employeeId,
      },
    });
  }, []);

  const handleSubmit = () => {
    const data = unpatch(
      snakecase({
        ...employeeFormData,
        workEmail: email,
      }),
    );

    props.onUpdateEmployee({
      statusUpdate: true,
      clientId: props.clientId,
      employeeId: props.employeeId,
      formData: data,
    });
    props.onClose();
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const isDisabled = () => {
    return !email || !emailPattern.test(email);
  };

  return (
    <Modal className="modal-nano">
      <ModalHeader toggle={props.onClose}>{props.t("SET_EMAIL_ADDRESS")}</ModalHeader>
      <ModalBody>
        <View gap={5} className="text-center" padding="0 1rem">
          <Text size={1.1}>{props.t("SET_EMAIL_MESSAGE")}</Text>
          <View direction="row" alignItems="center" gap={10}>
            <View direction="row" gap={1} alignItems="center">
              <Text color="charcoal">Email</Text>
              <Icon icon="asterisk" size="xs" className="text-warning" />
            </View>
            <TextField value={email} onChange={handleEmailChange} width="100%" />
          </View>
        </View>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-end">
        <Button color="secondary" onClick={props.onClose}>
          {props.t("CANCEL")}
        </Button>
        <Button disabled={isDisabled()} onClick={handleSubmit} materialIcon="replay_circle_filled">
          {props.t("SUBMIT")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const mapStateToProps = (state, ownProps): PropsFromState => ({
  employeeFormData: getEmployeeFormData(ownProps.employeeId)(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  getEmployees: employeeActionCreators.GetEmployees,
  onUpdateEmployee: employeeActionCreators.UpdateEmployeeDetails,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(SetMissingEmailModal);
